import React, { useEffect, useState, useRef, createContext } from 'react';
import { Offcanvas } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBars, faExpand, faCompress, faMeteor, faBell, faChartLine,
  faUser, faSignOutAlt, faCog, faSearch, faArrowLeft, faSync
} from '@fortawesome/free-solid-svg-icons';

import Sidebar from './Sidebar';
import Logo from '../../../assetes/logos.png';
import ProfilCandidat from '../erp/ProfilCandidat';
import SidebarRH from './SidebarRH';
import OffrePoster from '../erp/OffrePoster';

// ============================================================
// CONTEXTE PARTAGÉ POUR LE PROFIL CANDIDAT (refresh, source)
// ============================================================
export const CandidatProfilContext = createContext();

// ============================================================
// COMPOSANT PRINCIPAL
// ============================================================
const CustomCandidatProfil = ({ userId, fonction }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [theme] = useState('dark');
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [userData] = useState({ name: 'Administrateur', role: 'SUPER ADMIN' });
  const [loading, setLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const dropdownRef = useRef(null);

  // États pour le refresh global et la source
  const [refreshKey, setRefreshKey] = useState(0);
  const [source, setSource] = useState('cache');

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 300);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(console.error);
    } else {
      document.exitFullscreen();
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/');
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  // Forcer l'actualisation
  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  // Valeur du contexte
  const contextValue = {
    refreshKey,
    source,
    setSource,
    candidatId: id,
  };

  if (!id) {
    return (
      <div className="loading-screen">
        <p className="text-danger">Aucun identifiant de candidat fourni.</p>
        <button className="monetra-btn" onClick={handleGoBack}>Retour</button>
      </div>
    );
  }

  return (
    <>
      <style>{`
        /* ============================================================
           DARK BLUE GLASSMORPHISM – CUSTOM CANDIDAT PROFIL (amélioré)
           ============================================================ */
        :root {
          --accent-blue: #3b82f6;
          --accent-dark-blue: #1d4ed8;
          --accent-blue-dim: rgba(59, 130, 246, 0.10);
          --accent-blue-glow: rgba(59, 130, 246, 0.25);
          --bg-primary: #0b1428;
          --bg-gradient: radial-gradient(circle at 20% 30%, #132044, #0a0f1f);
          --card-bg: rgba(20, 40, 80, 0.55);
          --card-border: rgba(59, 130, 246, 0.15);
          --text-primary: #e0e7ff;
          --text-secondary: #94a3b8;
          --text-muted: #64748b;
          --header-height: 54px;
          --radius: 16px;
          --shadow: 0 12px 40px rgba(0,0,0,0.5);
        }

        body {
          background: var(--bg-gradient);
          color: var(--text-primary);
          font-family: 'Inter', sans-serif;
          margin: 0;
          min-height: 100vh;
        }

        .loading-screen {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100vh;
          background: var(--bg-gradient);
        }

        .spinner {
          width: 48px;
          height: 48px;
          border: 4px solid rgba(59, 130, 246, 0.08);
          border-top-color: var(--accent-blue);
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        @keyframes spin { to { transform: rotate(360deg); } }

        /* --- HEADER MOBILE --- */
        .app-header {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 1030;
          background: rgba(11, 20, 40, 0.75);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          padding: 12px 18px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          border-bottom: 1px solid rgba(59, 130, 246, 0.08);
          height: var(--header-height);
        }

        .app-header .brand {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .app-header .brand img {
          height: 32px;
          filter: drop-shadow(0 0 6px rgba(59, 130, 246, 0.2));
        }

        .app-header .brand .brand-name {
          font-weight: 700;
          font-size: 1.2rem;
          background: linear-gradient(135deg, var(--accent-blue), var(--accent-dark-blue));
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .app-header .actions {
          display: flex;
          gap: 12px;
        }

        .app-header .actions button {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.04);
          color: var(--text-primary);
          width: 38px;
          height: 38px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: 0.2s;
          font-size: 1.2rem;
        }

        .app-header .actions button:active {
          transform: scale(0.92);
          background: rgba(59, 130, 246, 0.12);
          border-color: rgba(59, 130, 246, 0.1);
        }

        .notif-badge {
          position: absolute;
          top: -2px;
          right: -2px;
          width: 16px;
          height: 16px;
          background: #ef4444;
          border-radius: 50%;
          font-size: 0.55rem;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #fff;
          border: 2px solid var(--bg-primary);
        }

        .notif-btn {
          position: relative;
        }

        /* --- TOPBAR DESKTOP (avec badge source et bouton refresh) --- */
        .desktop-topbar {
          display: none;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 32px;
          padding: 16px 24px;
          background: rgba(20, 40, 80, 0.3);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(59, 130, 246, 0.08);
          border-radius: 24px;
          box-shadow: 0 10px 40px rgba(0,0,0,0.4);
        }

        @media (min-width: 992px) {
          .desktop-topbar { display: flex; }
        }

        .topbar-left-nav {
          display: flex;
          gap: 12px;
          align-items: center;
        }

        .topbar-left-nav .page-title {
          font-size: 1.4rem;
          font-weight: 700;
          background: linear-gradient(135deg, #e0e7ff, #a5b4fc);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          margin: 0;
        }

        .topbar-right-actions {
          display: flex;
          align-items: center;
          gap: 14px;
          flex-wrap: wrap;
        }

        .badge-source {
          font-size: 11px;
          padding: 4px 12px;
          border-radius: 40px;
          background: rgba(59,130,246,0.15);
          color: var(--accent-blue);
          border: 1px solid rgba(59,130,246,0.12);
          white-space: nowrap;
        }
        .badge-source.live {
          background: rgba(46,204,113,0.15);
          color: #2ecc71;
          border-color: rgba(46,204,113,0.12);
        }
        .badge-source.warning {
          background: rgba(245,158,11,0.15);
          color: #f59e0b;
          border-color: rgba(245,158,11,0.12);
        }

        .monetra-btn {
          padding: 8px 18px;
          border-radius: 30px;
          border: 1px solid rgba(255,255,255,0.06);
          background: rgba(255,255,255,0.04);
          color: var(--text-primary);
          transition: all 0.25s ease;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-weight: 500;
          cursor: pointer;
          font-size: 0.9rem;
          white-space: nowrap;
        }

        .monetra-btn:hover {
          background: rgba(255,255,255,0.08);
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(0,0,0,0.2);
        }

        .monetra-btn.primary {
          background: var(--accent-blue);
          color: #fff;
          border: none;
          box-shadow: 0 6px 20px var(--accent-blue-glow);
        }

        .monetra-btn.primary:hover {
          background: var(--accent-dark-blue);
          box-shadow: 0 8px 28px rgba(59, 130, 246, 0.4);
        }

        .topbar-avatar-wrapper {
          position: relative;
        }

        .topbar-avatar {
          width: 42px;
          height: 42px;
          border-radius: 50%;
          background: linear-gradient(135deg, #1a2a4a, #2a4a7a);
          border: 2px solid var(--accent-blue);
          color: var(--text-primary);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          font-weight: 600;
          font-size: 1rem;
          transition: 0.2s;
          box-shadow: 0 0 20px rgba(59, 130, 246, 0.1);
        }

        .topbar-avatar:hover {
          transform: scale(1.05);
          box-shadow: 0 0 30px rgba(59, 130, 246, 0.25);
        }

        .profile-dropdown {
          position: absolute;
          top: calc(100% + 12px);
          right: 0;
          width: 200px;
          background: rgba(10, 20, 45, 0.95);
          backdrop-filter: blur(24px);
          -webkit-backdrop-filter: blur(24px);
          border: 1px solid rgba(59, 130, 246, 0.1);
          border-radius: 20px;
          box-shadow: 0 20px 60px rgba(0,0,0,0.6);
          padding: 8px;
          z-index: 1050;
          opacity: 0;
          visibility: hidden;
          transform: translateY(-8px);
          transition: all 0.3s cubic-bezier(0.2, 0.8, 0.2, 1);
          transform-origin: top right;
        }

        .profile-dropdown.open {
          opacity: 1;
          visibility: visible;
          transform: translateY(0);
        }

        .dropdown-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 14px;
          border-radius: 12px;
          color: var(--text-primary);
          text-decoration: none;
          transition: 0.2s;
          cursor: pointer;
        }

        .dropdown-item:hover {
          background: rgba(255,255,255,0.04);
        }

        .dropdown-divider {
          height: 1px;
          background: rgba(255,255,255,0.04);
          margin: 4px 10px;
        }

        .dropdown-logout {
          color: #f87171;
        }

        .dropdown-logout:hover {
          background: rgba(239, 68, 68, 0.08);
        }

        /* --- CONTENU PRINCIPAL --- */
        .main-content {
          padding: 16px;
          min-height: 80vh;
          padding-top: calc(var(--header-height) + 16px);
        }

        @media (min-width: 992px) {
          .app-header { display: none; }
          .main-content {
            margin-left: 280px;
            padding: 40px;
            padding-top: 40px;
          }
        }

        /* --- CARTES GLASS POUR LES ENFANTS --- */
        .glass-card {
          background: var(--card-bg);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid var(--card-border);
          border-radius: var(--radius);
          padding: 1.5rem;
          box-shadow: var(--shadow);
          transition: all 0.25s ease;
          margin-bottom: 24px;
        }

        .glass-card:hover {
          border-color: rgba(59, 130, 246, 0.3);
          box-shadow: 0 12px 40px rgba(59, 130, 246, 0.08);
          transform: translateY(-2px);
        }

        .page-title {
          font-size: 1.6rem;
          font-weight: 700;
          background: linear-gradient(135deg, #e0e7ff, #94a3b8);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .page-sub {
          font-size: 0.7rem;
          text-transform: uppercase;
          letter-spacing: 0.8px;
          color: var(--text-secondary);
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .update-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: rgba(59, 130, 246, 0.08);
          border: 1px solid rgba(59, 130, 246, 0.08);
          border-radius: 20px;
          padding: 4px 12px;
          font-size: 0.6rem;
          color: var(--text-secondary);
        }

        .update-badge svg {
          color: var(--accent-blue);
        }

        .dashboard-header {
          margin-bottom: 1.5rem;
        }

        .offcanvas-glass {
          background: rgba(10, 20, 45, 0.95) !important;
          backdrop-filter: blur(24px) !important;
          -webkit-backdrop-filter: blur(24px) !important;
          border-right: 1px solid rgba(59, 130, 246, 0.1) !important;
        }

        .offcanvas-glass .btn-close {
          filter: invert(1) brightness(200%);
          opacity: 0.5;
        }

        .sidebar-desktop {
          position: fixed;
          top: 0;
          left: 0;
          width: 280px;
          height: 100vh;
          z-index: 1040;
          display: none;
        }

        @media (min-width: 992px) {
          .sidebar-desktop { display: block; }
        }

        .d-flex {
          display: flex;
        }
        .flex-wrap {
          flex-wrap: wrap;
        }
        .align-items-center {
          align-items: center;
        }
        .justify-content-between {
          justify-content: space-between;
        }
        .gap-2 {
          gap: 0.5rem;
        }
        .gap-3 {
          gap: 1rem;
        }
        .me-1 {
          margin-right: 0.25rem;
        }
        .mb-2 {
          margin-bottom: 0.5rem;
        }
      `}</style>

      {/* Sidebar Desktop */}
      <div className="sidebar-desktop">
        {fonction === 'SUPER ADMIN' && <Sidebar userId={userId} theme={theme} fonction={fonction} />}
        {fonction === 'DSI' && <Sidebar userId={userId} theme={theme} fonction={fonction} />}
        {fonction === 'RH' && <SidebarRH userId={userId} theme={theme} fonction={fonction} />}
      </div>

      {/* Sidebar Mobile */}
      <Offcanvas show={showMobileMenu} onHide={() => setShowMobileMenu(false)} placement="start" className="offcanvas-glass">
        <Offcanvas.Header closeButton closeVariant="white">
          <Offcanvas.Title className="text-white fw-bold">
            <img src={Logo} alt="logo" height="28" className="me-2" /> Menu
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="p-0">
          {fonction === 'RH' && (
            <SidebarRH userId={userId} theme={theme} fonction={fonction} onLinkClick={() => setShowMobileMenu(false)} />
          )}
          {fonction === 'SUPER ADMIN' && (
            <Sidebar userId={userId} theme={theme} fonction={fonction} onLinkClick={() => setShowMobileMenu(false)} />
          )}
          {fonction === 'DSI' && (
            <Sidebar userId={userId} theme={theme} fonction={fonction} onLinkClick={() => setShowMobileMenu(false)} />
          )}
        </Offcanvas.Body>
      </Offcanvas>

      {/* Header Mobile */}
      <header className="app-header d-lg-none">
        <div className="brand">
          <img src={Logo} alt="logo" />
          <span className="brand-name">Profil Candidat</span>
        </div>
        <div className="actions">
          <button onClick={handleGoBack} title="Retour">
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
          <button onClick={toggleFullscreen} title="Plein écran">
            <FontAwesomeIcon icon={isFullscreen ? faCompress : faExpand} />
          </button>
          <button onClick={() => setShowMobileMenu(true)}>
            <FontAwesomeIcon icon={faBars} />
          </button>
        </div>
      </header>

      {/* Contenu principal */}
      <main className="main-content">
        {/* Topbar Desktop avec titre, badge source, bouton refresh et retour */}
        <div className="desktop-topbar">
          <div className="topbar-left-nav">
            <button className="monetra-btn" onClick={handleGoBack}>
              <FontAwesomeIcon icon={faArrowLeft} /> Retour
            </button>
            <h2 className="page-title">👤 Profil candidat #{id}</h2>
          </div>
          <div className="topbar-right-actions">
            <span className={`badge-source ${source === 'live' ? 'live' : source === 'cache (périmé)' ? 'warning' : ''}`}>
              {source === 'cache' ? '📦 Cache' :
               source === 'cache (périmé)' ? '⚠️ Cache périmé' :
               source === 'live' ? '🔄 Live' :
               source === 'live (forcé)' ? '🔄 Forcé' : '📦 Cache'}
            </span>
            <button className="monetra-btn primary" onClick={handleRefresh}>
              <FontAwesomeIcon icon={faSync} /> Actualiser
            </button>
            <button className="monetra-btn" onClick={toggleFullscreen} title={isFullscreen ? 'Quitter le plein écran' : 'Plein écran'}>
              <FontAwesomeIcon icon={isFullscreen ? faCompress : faExpand} />
            </button>
            <div className="topbar-avatar-wrapper" ref={dropdownRef}>
              <div className="topbar-avatar" onClick={() => setShowProfileMenu(!showProfileMenu)}>
                {userData.name.charAt(0)}
              </div>
              <div className={`profile-dropdown ${showProfileMenu ? 'open' : ''}`}>
                <div className="dropdown-item">
                  <FontAwesomeIcon icon={faUser} style={{ width: '18px' }} />
                  <span>Mon Profil</span>
                </div>
                <div className="dropdown-divider" />
                <div className="dropdown-item dropdown-logout" onClick={handleLogout}>
                  <FontAwesomeIcon icon={faSignOutAlt} style={{ width: '18px' }} />
                  <span>Déconnexion</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* En-tête de page */}
        <div className="dashboard-header">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
            <div>
              <div className="page-sub">
                <FontAwesomeIcon icon={faChartLine} className="me-1" /> Profil candidat
              </div>
              <h1 className="page-title">Fiche détaillée</h1>
            </div>
            <div className="update-badge">
              <FontAwesomeIcon icon={faMeteor} />
              <span>ID #{id}</span>
            </div>
          </div>
        </div>

        {/* Composants enfants enveloppés par le Provider */}
        <CandidatProfilContext.Provider value={contextValue}>
          <div className="glass-card">
           
           <div className="row">
            <div className="col-md-5"> <ProfilCandidat candidatId={id} /></div>
            <div className="col-md-7"><OffrePoster candidatId={id} /></div>
           </div>
          </div>
         
        </CandidatProfilContext.Provider>
      </main>
    </>
  );
};

export default CustomCandidatProfil;